import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const WORKER_TYPE_LABELS = {
  t4_employee: "T4 Employee",
  t4a_contractor: "T4A Contractor",
  w2_employee: "W2 Employee",
  "1099_contractor": "1099 Contractor",
};

const DEPARTMENTS = [
  { value: "dispatch", label: "Dispatch Operations" },
  { value: "accounting", label: "Accounting" },
  { value: "sales", label: "Sales/Business Dev" },
  { value: "hr", label: "Human Resources" },
  { value: "maintenance", label: "Fleet Maintenance" },
  { value: "safety", label: "Fleet Safety" },
  { value: "operations", label: "Operations/Executive" }
];

const BASE_ROLES = [
  { value: "admin", label: "Administrator (Full Access)" },
  { value: "manager", label: "Manager (Department Access)" },
  { value: "dispatcher", label: "Dispatcher (Dispatch Ops)" },
  { value: "driver", label: "Driver (Mobile App Access)" },
  { value: "billing", label: "Billing/Accounts (Financial Access)" },
  { value: "viewer", label: "Viewer (Read Only)" }
];

const DOCUMENT_TYPES = [
  { value: "passport", label: "Passport / ID Card" },
  { value: "tax_form", label: "Tax Form (TD1 / W-4)" },
  { value: "direct_deposit", label: "Direct Deposit Details" },
  { value: "drivers_license", label: "Driver's License (Class 1/A/D)" },
  { value: "medical_card", label: "Medical Card / Examiner Certificate" },
  { value: "drug_test", label: "DOT Drug & Alcohol Test" },
  { value: "background_check", label: "Criminal Record Check" },
  { value: "cvor_certificate", label: "CVOR / NSC Certificate" },
  { value: "liability_insurance", label: "Commercial Liability Insurance" },
  { value: "cargo_insurance", label: "Cargo Insurance Certificate" }
];

const HRDepartment = () => {
  const { fetchWithAuth } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Data State
  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter/Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [workerTypeFilter, setWorkerTypeFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');

  // Modal Dialog States
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showPayrollModal, setShowPayrollModal] = useState(false);

  // Selected Item States for Actions
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Forms State
  const [staffForm, setStaffForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    custom_role_id: '',
    worker_type: 't4_employee',
    hire_date: new Date().toISOString().split('T')[0],
    status: 'active',
    create_user_account: false,
    password: '',
    payroll_profile: {
      pay_type: 'salary',
      pay_rate: 0,
      payment_frequency: 'bi_weekly',
      payment_method: 'direct_deposit'
    }
  });

  const [roleForm, setRoleForm] = useState({
    title: '',
    description: '',
    department: 'dispatch',
    base_role: 'manager',
    required_documents: []
  });

  const [docUploadForm, setDocUploadForm] = useState({
    expiry_date: '',
    file_name: '',
    isUploading: false
  });

  const [payrollForm, setPayrollForm] = useState({
    pay_type: 'salary',
    pay_rate: 0,
    payment_frequency: 'bi_weekly',
    payment_method: 'direct_deposit'
  });

  // Load Initial Data
  const loadData = async () => {
    setLoading(true);
    try {
      const rolesRes = await fetchWithAuth(`${BACKEND_URL}/api/hr/roles`);
      const staffRes = await fetchWithAuth(`${BACKEND_URL}/api/hr/staff`);
      
      if (rolesRes.ok && staffRes.ok) {
        const rolesData = await rolesRes.json();
        const staffData = await staffRes.json();
        setRoles(rolesData);
        setStaff(staffData);
        
        // Auto-select first role if form is empty
        if (rolesData.length > 0 && !staffForm.custom_role_id) {
          setStaffForm(prev => ({ ...prev, custom_role_id: rolesData[0].id }));
        }
      }
    } catch (err) {
      console.error("Error loading HR data", err);
      toast.error("Failed to load HR workspace data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle Staff Creation
  const handleAddStaffSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/hr/staff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(staffForm)
      });
      if (res.ok) {
        toast.success("Staff member added successfully!");
        setShowAddStaffModal(false);
        // Reset form
        setStaffForm({
          full_name: '',
          email: '',
          phone: '',
          custom_role_id: roles[0]?.id || '',
          worker_type: 't4_employee',
          hire_date: new Date().toISOString().split('T')[0],
          status: 'active',
          create_user_account: false,
          password: '',
          payroll_profile: {
            pay_type: 'salary',
            pay_rate: 0,
            payment_frequency: 'bi_weekly',
            payment_method: 'direct_deposit'
          }
        });
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add staff member");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred");
    }
  };

  // Handle Role Creation
  const handleAddRoleSubmit = async (e) => {
    e.preventDefault();
    if (!roleForm.title.trim()) {
      toast.error("Role title is required");
      return;
    }
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/hr/roles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(roleForm)
      });
      if (res.ok) {
        toast.success("Custom role created!");
        setShowAddRoleModal(false);
        setRoleForm({
          title: '',
          description: '',
          department: 'dispatch',
          base_role: 'manager',
          required_documents: []
        });
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create role");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred");
    }
  };

  // Handle Document Upload
  const handleDocUploadSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDoc) return;
    
    setDocUploadForm(prev => ({ ...prev, isUploading: true }));
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/hr/staff/${selectedStaff.id}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          doc_type: selectedDoc.doc_type,
          label: selectedDoc.label,
          expiry_date: docUploadForm.expiry_date || null,
          file_url: "/assets/placeholder-document.pdf"
        })
      });
      
      if (res.ok) {
        toast.success(`Document uploaded for review`);
        setShowDocModal(false);
        setDocUploadForm({ expiry_date: '', file_name: '', isUploading: false });
        loadData();
      } else {
        toast.error("Failed to upload document");
      }
    } catch (err) {
      console.error(err);
      toast.error("Upload error");
    } finally {
      setDocUploadForm(prev => ({ ...prev, isUploading: false }));
    }
  };

  // Verify Document Action (Admin)
  const handleVerifyDocument = async (statusVal) => {
    if (!selectedDoc || !selectedStaff) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/hr/staff/${selectedStaff.id}/documents/${selectedDoc.id}/verify`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: statusVal,
          expiry_date: docUploadForm.expiry_date || selectedDoc.expiry_date
        })
      });
      if (res.ok) {
        toast.success(`Document status updated to: ${statusVal}`);
        setShowDocModal(false);
        loadData();
      } else {
        toast.error("Failed to verify document");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Delete Staff member
  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm("Are you sure you want to remove this staff member? This deletes their credentials and document history.")) return;
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/hr/staff/${staffId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success("Staff profile removed");
        loadData();
      } else {
        toast.error("Failed to delete staff member");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Handle Payroll Submit
  const handlePayrollSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetchWithAuth(`${BACKEND_URL}/api/hr/staff/${selectedStaff.id}/payroll`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payrollForm)
      });
      if (res.ok) {
        toast.success("Payroll profile updated!");
        setShowPayrollModal(false);
        loadData();
      } else {
        toast.error("Failed to update payroll profile");
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Toggle Document List Checklist in Role Form
  const toggleDocInRoleForm = (docVal) => {
    setRoleForm(prev => {
      const exists = prev.required_documents.includes(docVal);
      return {
        ...prev,
        required_documents: exists
          ? prev.required_documents.filter(d => d !== docVal)
          : [...prev.required_documents, docVal]
      };
    });
  };

  // Filters computed lists
  const filteredStaff = useMemo(() => {
    return staff.filter(s => {
      const matchesSearch = s.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            s.phone?.includes(searchTerm);
      
      const matchesRole = roleFilter === 'all' || s.custom_role_id === roleFilter;
      const matchesType = workerTypeFilter === 'all' || s.worker_type === workerTypeFilter;
      
      const matchesCompliance = complianceFilter === 'all' || 
                                 (complianceFilter === 'compliant' && s.compliance_status === 'verified') ||
                                 (complianceFilter === 'pending' && s.compliance_status === 'pending_review') ||
                                 (complianceFilter === 'expired' && s.compliance_status === 'expired');
                                 
      return matchesSearch && matchesRole && matchesType && matchesCompliance;
    });
  }, [staff, searchTerm, roleFilter, workerTypeFilter, complianceFilter]);

  // Statistics Summary
  const stats = useMemo(() => {
    const totalCount = staff.length;
    const compliantCount = staff.filter(s => s.compliance_status === 'verified').length;
    const expiredCount = staff.filter(s => s.compliance_status === 'expired').length;
    
    // Calculate estimated monthly payroll
    let estimatedMonthlyPayroll = 0;
    staff.forEach(s => {
      if (!s.payroll_profile) return;
      const rate = s.payroll_profile.pay_rate || 0;
      const freq = s.payroll_profile.payment_frequency;
      const type = s.payroll_profile.pay_type;
      
      if (type === 'salary') {
        estimatedMonthlyPayroll += rate / 12; // annualized base
      } else if (type === 'hourly') {
        estimatedMonthlyPayroll += rate * 160; // 160 hours standard month
      } else {
        estimatedMonthlyPayroll += rate * 20; // estimated per-mile or per-load runs
      }
    });

    return {
      total: totalCount,
      complianceRate: totalCount > 0 ? Math.round((compliantCount / totalCount) * 100) : 100,
      alerts: expiredCount,
      monthlyPayroll: Math.round(estimatedMonthlyPayroll)
    };
  }, [staff]);

  const getComplianceBadge = (statusVal) => {
    const configs = {
      verified: { label: 'Compliant', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
      pending_review: { label: 'Pending Review', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
      expired: { label: 'Action Required', color: 'bg-red-500/10 text-red-500 border-red-500/20' }
    };
    const c = configs[statusVal] || configs.expired;
    return <Badge className={`${c.color} border px-2.5 py-0.5 rounded-full text-xs font-semibold`}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Title & Stats */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <i className="fas fa-users-cog text-primary"></i> Human Resources Workspace
          </h2>
          <p className="text-sm text-muted-foreground">Manage dynamic company roles, staff roster, document compliance, and payroll.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setShowAddStaffModal(true)} className="bg-primary hover:bg-primary/90 text-white rounded-xl px-4">
            <i className="fas fa-user-plus mr-2"></i> Add Staff Member
          </Button>
          <Button onClick={() => setShowAddRoleModal(true)} variant="outline" className="border-border hover:bg-muted rounded-xl px-4">
            <i className="fas fa-plus-circle mr-2"></i> Create Custom Role
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-card border border-border shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Total Staff</p>
                <h3 className="text-3xl font-bold text-foreground mt-1">{stats.total}</h3>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary text-xl">
                <i className="fas fa-users"></i>
              </div>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Employees and Owner Operators
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Compliance Rate</p>
                <h3 className="text-3xl font-bold text-foreground mt-1">{stats.complianceRate}%</h3>
              </div>
              <div className="h-12 w-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-500 text-xl">
                <i className="fas fa-check-shield"></i>
              </div>
            </div>
            <div className="mt-2 w-full bg-muted rounded-full h-1.5 overflow-hidden">
              <div className="bg-emerald-500 h-full rounded-full" style={{ width: `${stats.complianceRate}%` }}></div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Compliance Alerts</p>
                <h3 className="text-3xl font-bold text-foreground mt-1">{stats.alerts}</h3>
              </div>
              <div className={`h-12 w-12 ${stats.alerts > 0 ? 'bg-red-500/10 text-red-500' : 'bg-muted text-muted-foreground'} rounded-xl flex items-center justify-center text-xl`}>
                <i className="fas fa-exclamation-triangle"></i>
              </div>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {stats.alerts > 0 ? "Expired or missing documents" : "All clear"}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase">Est. Monthly Payroll</p>
                <h3 className="text-3xl font-bold text-foreground mt-1">${stats.monthlyPayroll.toLocaleString()}</h3>
              </div>
              <div className="h-12 w-12 bg-blue-500/10 rounded-xl flex items-center justify-center text-blue-500 text-xl">
                <i className="fas fa-file-invoice-dollar"></i>
              </div>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Based on active pay rate profiles
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs Container */}
      <Tabs defaultValue="staff" className="w-full">
        <TabsList className="flex bg-muted p-1 rounded-xl w-fit mb-6">
          <TabsTrigger value="staff" className="rounded-lg px-4 py-2 text-sm font-medium">
            <i className="fas fa-address-book mr-2"></i> Staff Roster
          </TabsTrigger>
          <TabsTrigger value="roles" className="rounded-lg px-4 py-2 text-sm font-medium">
            <i className="fas fa-tags mr-2"></i> Custom Roles
          </TabsTrigger>
          <TabsTrigger value="compliance" className="rounded-lg px-4 py-2 text-sm font-medium">
            <i className="fas fa-shield-alt mr-2"></i> Document Compliance
          </TabsTrigger>
          <TabsTrigger value="payroll" className="rounded-lg px-4 py-2 text-sm font-medium">
            <i className="fas fa-wallet mr-2"></i> Payroll Configuration
          </TabsTrigger>
        </TabsList>

        {/* STAFF ROSTER TAB */}
        <TabsContent value="staff" className="space-y-4 outline-none">
          <div className="flex flex-col md:flex-row md:items-center gap-4 bg-card border border-border p-4 rounded-2xl shadow-sm">
            <div className="flex-1 relative">
              <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
              <Input
                type="text"
                placeholder="Search staff by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-border bg-background focus-visible:ring-primary focus-visible:ring-1 rounded-xl h-10 w-full"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-48 border-border rounded-xl h-10 bg-background text-foreground">
                  <SelectValue placeholder="Filter by Role" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>{role.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={workerTypeFilter} onValueChange={setWorkerTypeFilter}>
                <SelectTrigger className="w-48 border-border rounded-xl h-10 bg-background text-foreground">
                  <SelectValue placeholder="Filter by Class" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="all">All Classifications</SelectItem>
                  {Object.entries(WORKER_TYPE_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={complianceFilter} onValueChange={setComplianceFilter}>
                <SelectTrigger className="w-48 border-border rounded-xl h-10 bg-background text-foreground">
                  <SelectValue placeholder="Compliance Status" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="all">All Compliance</SelectItem>
                  <SelectItem value="compliant">Compliant Only</SelectItem>
                  <SelectItem value="pending">Pending Review</SelectItem>
                  <SelectItem value="expired">Action Required</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12 text-muted-foreground text-sm font-medium">
              <i className="fas fa-spinner fa-spin mr-2"></i> Loading staff roster...
            </div>
          ) : filteredStaff.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border border-dashed rounded-2xl text-muted-foreground">
              <i className="fas fa-users fa-2x mb-3 text-muted-foreground/50"></i>
              <p className="font-semibold text-foreground">No staff members found</p>
              <p className="text-sm mt-1">Try resetting your filters or add a new staff member to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredStaff.map((member) => (
                <Card key={member.id} className="bg-card border border-border hover:shadow-md transition-shadow duration-300 rounded-2xl relative overflow-hidden group">
                  <CardContent className="p-5 space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary">
                          {member.full_name.split(' ').map(n=>n[0]).join('').toUpperCase()}
                        </div>
                        <div>
                          <h4 className="font-bold text-foreground text-base leading-tight">{member.full_name}</h4>
                          <span className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
                            <i className="fas fa-calendar-alt text-[10px]"></i> Hired: {member.hire_date}
                          </span>
                        </div>
                      </div>
                      <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none font-medium px-2 py-0.5 rounded text-xs">
                        {member.role_title}
                      </Badge>
                    </div>

                    <div className="border-t border-border/60 my-2 pt-3 space-y-2 text-sm">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Classification</span>
                        <span className="font-medium text-foreground">{WORKER_TYPE_LABELS[member.worker_type]}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Compliance Status</span>
                        {getComplianceBadge(member.compliance_status)}
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Login Access</span>
                        <Badge className={`${member.user_id ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-muted text-muted-foreground'} px-1.5 py-0.2 rounded border text-[10px]`}>
                          {member.user_id ? 'Linked' : 'Roster Only'}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex justify-between items-center gap-2 pt-1">
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => {
                            setSelectedStaff(member);
                            setPayrollForm(member.payroll_profile || {
                              pay_type: 'salary',
                              pay_rate: 0,
                              payment_frequency: 'bi_weekly',
                              payment_method: 'direct_deposit'
                            });
                            setShowPayrollModal(true);
                          }} 
                          variant="outline" 
                          size="sm" 
                          className="h-8 rounded-lg text-xs font-semibold px-2.5 border-border hover:bg-muted"
                          title="Payroll Settings"
                        >
                          <i className="fas fa-wallet mr-1 text-primary"></i> Pay Rate
                        </Button>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          onClick={() => {
                            setSelectedStaff(member);
                            // Open compliance portal for the member
                            document.querySelector('[value="compliance"]').click();
                            setSearchTerm(member.full_name);
                          }} 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 rounded-lg text-xs text-muted-foreground hover:text-foreground px-2"
                        >
                          <i className="fas fa-file-shield text-xs mr-1"></i> Docs
                        </Button>
                        <Button 
                          onClick={() => handleDeleteStaff(member.id)} 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 rounded-lg text-destructive hover:bg-red-500/10 p-0"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CUSTOM ROLES TAB */}
        <TabsContent value="roles" className="space-y-4 outline-none">
          {loading ? (
            <div className="flex justify-center items-center py-12 text-muted-foreground text-sm font-medium">
              <i className="fas fa-spinner fa-spin mr-2"></i> Loading custom roles...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((role) => (
                <Card key={role.id} className="bg-card border border-border rounded-2xl relative overflow-hidden flex flex-col justify-between">
                  <CardHeader className="p-5 pb-2">
                    <div className="flex justify-between items-start">
                      <Badge className="bg-muted text-muted-foreground hover:bg-muted font-medium capitalize px-2 py-0.5 rounded text-xs mb-1">
                        {role.department}
                      </Badge>
                      <Badge className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 border-none font-medium px-2 py-0.5 rounded text-[10px] uppercase">
                        {role.base_role}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-bold text-foreground mt-1 leading-tight">{role.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-5 pt-0 space-y-4 flex-1 flex flex-col justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{role.description || "No description provided."}</p>
                      
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Required Documents Checklist</p>
                        {role.required_documents.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic">No documentation checklists required for this role.</p>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {role.required_documents.map(doc => (
                              <Badge key={doc} variant="outline" className="text-[10px] py-0.5 px-2 border-border bg-background text-muted-foreground capitalize font-normal">
                                {doc.replace("_", " ")}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="border-t border-border/60 pt-4 flex justify-between items-center text-xs mt-4">
                      <span className="text-muted-foreground">Created: {role.created_at.split('T')[0]}</span>
                      <Button 
                        onClick={async () => {
                          if (window.confirm(`Are you sure you want to delete the ${role.title} role?`)) {
                            try {
                              const res = await fetchWithAuth(`${BACKEND_URL}/api/hr/roles/${role.id}`, { method: 'DELETE' });
                              if (res.ok) {
                                toast.success("Role deleted");
                                loadData();
                              } else {
                                const err = await res.json();
                                toast.error(err.detail || "Failed to delete role");
                              }
                            } catch (e) {
                              toast.error("Error occurred");
                            }
                          }
                        }} 
                        variant="ghost" 
                        className="text-destructive h-7 hover:bg-red-500/10 text-xs px-2.5 rounded-lg"
                      >
                        <i className="fas fa-trash-alt mr-1"></i> Delete Role
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* DOCUMENT COMPLIANCE MATRIX TAB */}
        <TabsContent value="compliance" className="space-y-4 outline-none">
          <div className="flex flex-col md:flex-row md:items-center gap-4 bg-card border border-border p-4 rounded-2xl shadow-sm">
            <div className="flex-1 relative">
              <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm"></i>
              <Input
                type="text"
                placeholder="Search staff members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-border bg-background focus-visible:ring-primary focus-visible:ring-1 rounded-xl h-10 w-full"
              />
            </div>
          </div>

          <Card className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden">
            <div className="overflow-x-auto w-full">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide w-[200px]">Staff Member</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide w-[180px]">Role / Job Title</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide">Required Checklists Compliance Matrix</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {loading ? (
                    <tr>
                      <td colSpan="3" className="p-12 text-center text-muted-foreground text-sm font-medium">
                        <i className="fas fa-spinner fa-spin mr-2"></i> Loading compliance data...
                      </td>
                    </tr>
                  ) : filteredStaff.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="p-12 text-center text-muted-foreground text-sm">
                        No staff matching filters.
                      </td>
                    </tr>
                  ) : (
                    filteredStaff.map((member) => (
                      <tr key={member.id} className="hover:bg-muted/10 transition-colors">
                        <td className="p-4 align-top">
                          <div className="font-bold text-foreground text-sm leading-tight">{member.full_name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{member.email}</div>
                        </td>
                        <td className="p-4 align-top">
                          <span className="text-xs font-semibold px-2 py-0.5 bg-primary/10 text-primary border-none rounded">
                            {member.role_title}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {member.documents && member.documents.length > 0 ? (
                              member.documents.map(doc => {
                                const statusConfigs = {
                                  verified: { label: 'Verified', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', icon: 'fa-check-circle' },
                                  pending_review: { label: 'In Review', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20', icon: 'fa-clock' },
                                  expired: { label: 'Expired', color: 'bg-red-500/10 text-red-500 border-red-500/20', icon: 'fa-exclamation-circle' },
                                  missing: { label: 'Missing', color: 'bg-red-500/10 text-red-500 border-red-500/20', icon: 'fa-times' }
                                };
                                const sc = statusConfigs[doc.status] || statusConfigs.missing;
                                return (
                                  <div 
                                    key={doc.id}
                                    onClick={() => {
                                      setSelectedStaff(member);
                                      setSelectedDoc(doc);
                                      setDocUploadForm({ expiry_date: doc.expiry_date || '', file_name: doc.file_url ? 'file_attached.pdf' : '', isUploading: false });
                                      setShowDocModal(true);
                                    }}
                                    className="p-3 bg-background border border-border rounded-xl flex flex-col justify-between h-20 cursor-pointer hover:border-primary/50 hover:shadow-sm transition-all group"
                                  >
                                    <div className="text-[10px] font-bold text-foreground/80 line-clamp-1 leading-tight group-hover:text-primary">
                                      {doc.label}
                                    </div>
                                    <div className="flex items-center justify-between text-xs mt-1.5">
                                      <span className={`text-[10px] px-1.5 py-0.2 rounded border ${sc.color} flex items-center gap-1 font-medium`}>
                                        <i className={`fas ${sc.icon}`}></i> {sc.label}
                                      </span>
                                      {doc.expiry_date && (
                                        <span className="text-[9px] text-muted-foreground tracking-tight">
                                          Exp: {doc.expiry_date}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-xs text-muted-foreground italic col-span-5">
                                No required documents configured for this role.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* PAYROLL CONFIGURATION TAB */}
        <TabsContent value="payroll" className="space-y-4 outline-none">
          <Card className="bg-card border border-border shadow-sm rounded-2xl overflow-hidden">
            <div className="overflow-x-auto w-full">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-muted/50 border-b border-border">
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide">Staff Member</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide">Class</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide">Pay Type</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide">Pay Rate</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide">Frequency</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide">Method</th>
                    <th className="p-4 text-xs font-bold text-foreground uppercase tracking-wide text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60 text-sm">
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="p-12 text-center text-muted-foreground text-sm font-medium">
                        <i className="fas fa-spinner fa-spin mr-2"></i> Loading payroll config...
                      </td>
                    </tr>
                  ) : staff.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-12 text-center text-muted-foreground text-sm">
                        No staff profiles configured.
                      </td>
                    </tr>
                  ) : (
                    staff.map((member) => (
                      <tr key={member.id} className="hover:bg-muted/10 transition-colors">
                        <td className="p-4 font-bold text-foreground">
                          {member.full_name}
                          <div className="text-xs text-muted-foreground font-normal">{member.role_title}</div>
                        </td>
                        <td className="p-4 font-medium text-foreground">{WORKER_TYPE_LABELS[member.worker_type]}</td>
                        <td className="p-4 capitalize text-foreground">{member.payroll_profile?.pay_type || "salary"}</td>
                        <td className="p-4 font-bold text-foreground">
                          {member.payroll_profile?.pay_type === 'salary' && `$${(member.payroll_profile?.pay_rate || 0).toLocaleString()} / yr`}
                          {member.payroll_profile?.pay_type === 'hourly' && `$${member.payroll_profile?.pay_rate || 0} / hr`}
                          {member.payroll_profile?.pay_type === 'per_mile' && `$${member.payroll_profile?.pay_rate || 0} / mi`}
                          {member.payroll_profile?.pay_type === 'per_load' && `${member.payroll_profile?.pay_rate || 0}% of load`}
                          {member.payroll_profile?.pay_type === 'commission' && `${member.payroll_profile?.pay_rate || 0}% comm.`}
                          {!member.payroll_profile && "Not Configured"}
                        </td>
                        <td className="p-4 text-foreground capitalize">{member.payroll_profile?.payment_frequency.replace('_', ' ') || "bi weekly"}</td>
                        <td className="p-4 text-foreground capitalize">{member.payroll_profile?.payment_method.replace('_', ' ') || "direct deposit"}</td>
                        <td className="p-4 text-right">
                          <Button 
                            onClick={() => {
                              setSelectedStaff(member);
                              setPayrollForm(member.payroll_profile || {
                                pay_type: 'salary',
                                pay_rate: 0,
                                payment_frequency: 'bi_weekly',
                                payment_method: 'direct_deposit'
                              });
                              setShowPayrollModal(true);
                            }} 
                            variant="outline" 
                            size="sm" 
                            className="h-8 rounded-lg text-xs font-semibold px-2.5 border-border hover:bg-muted"
                          >
                            <i className="fas fa-edit mr-1"></i> Edit Compensation
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* DIALOG MODAL - ADD STAFF MEMBER */}
      <Dialog open={showAddStaffModal} onOpenChange={setShowAddStaffModal}>
        <DialogContent className="max-w-2xl bg-card border-border text-foreground max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <i className="fas fa-user-plus text-primary"></i> Add New Staff Profile
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStaffSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={staffForm.full_name}
                  onChange={(e) => setStaffForm(prev=>({ ...prev, full_name: e.target.value }))}
                  required
                  placeholder="e.g. John Doe"
                  className="border-border rounded-xl bg-background text-foreground h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={staffForm.email}
                  onChange={(e) => setStaffForm(prev=>({ ...prev, email: e.target.value }))}
                  required
                  placeholder="e.g. john@company.com"
                  className="border-border rounded-xl bg-background text-foreground h-10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={staffForm.phone || ''}
                  onChange={(e) => setStaffForm(prev=>({ ...prev, phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                  className="border-border rounded-xl bg-background text-foreground h-10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="custom_role">Job Role Title</Label>
                <Select 
                  value={staffForm.custom_role_id} 
                  onValueChange={(val) => setStaffForm(prev=>({ ...prev, custom_role_id: val }))}
                >
                  <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                    <SelectValue placeholder="Select custom role" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>{role.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="worker_type">Worker Classification</Label>
                <Select 
                  value={staffForm.worker_type} 
                  onValueChange={(val) => setStaffForm(prev=>({ ...prev, worker_type: val }))}
                >
                  <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                    <SelectValue placeholder="Select worker class" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    {Object.entries(WORKER_TYPE_LABELS).map(([val, label]) => (
                      <SelectItem key={val} value={val}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hire_date">Hire / Effective Date</Label>
                <Input
                  id="hire_date"
                  type="date"
                  value={staffForm.hire_date}
                  onChange={(e) => setStaffForm(prev=>({ ...prev, hire_date: e.target.value }))}
                  required
                  className="border-border rounded-xl bg-background text-foreground h-10"
                />
              </div>
            </div>

            {/* Compensation section */}
            <div className="border-t border-border/60 pt-4 space-y-4">
              <h5 className="font-bold text-sm text-foreground">Compensation Profile</h5>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pay Type</Label>
                  <Select 
                    value={staffForm.payroll_profile.pay_type} 
                    onValueChange={(val) => setStaffForm(prev=>({
                      ...prev,
                      payroll_profile: { ...prev.payroll_profile, pay_type: val }
                    }))}
                  >
                    <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                      <SelectValue placeholder="Pay Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border text-foreground">
                      <SelectItem value="salary">Salary (Flat / yr)</SelectItem>
                      <SelectItem value="hourly">Hourly Rate</SelectItem>
                      <SelectItem value="per_mile">Per Mile Payout</SelectItem>
                      <SelectItem value="per_load">Per Load (% of Revenue)</SelectItem>
                      <SelectItem value="commission">Commission %</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Compensation Rate</Label>
                  <Input
                    type="number"
                    value={staffForm.payroll_profile.pay_rate}
                    onChange={(e) => setStaffForm(prev=>({
                      ...prev,
                      payroll_profile: { ...prev.payroll_profile, pay_rate: parseFloat(e.target.value) || 0 }
                    }))}
                    placeholder="e.g. 50000"
                    className="border-border rounded-xl bg-background text-foreground h-10"
                  />
                </div>
              </div>
            </div>

            {/* System login creation toggle */}
            <div className="border-t border-border/60 pt-4 space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="create_user_account"
                  checked={staffForm.create_user_account}
                  onChange={(e) => setStaffForm(prev=>({ ...prev, create_user_account: e.target.checked }))}
                  className="rounded border-border text-primary focus:ring-primary h-4 w-4 bg-background"
                />
                <Label htmlFor="create_user_account" className="font-semibold cursor-pointer">Create System User Login Credentials</Label>
              </div>
              
              {staffForm.create_user_account && (
                <div className="space-y-2">
                  <Label htmlFor="password">Temporary Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={staffForm.password}
                    onChange={(e) => setStaffForm(prev=>({ ...prev, password: e.target.value }))}
                    required={staffForm.create_user_account}
                    placeholder="Enter password (minimum 6 characters)"
                    className="border-border rounded-xl bg-background text-foreground h-10"
                  />
                </div>
              )}
            </div>

            <DialogFooter className="border-t border-border/60 pt-4 mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddStaffModal(false)} className="rounded-xl border-border hover:bg-muted">
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90 text-white rounded-xl px-5">
                Save Staff Profile
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIALOG MODAL - CREATE CUSTOM ROLE */}
      <Dialog open={showAddRoleModal} onOpenChange={setShowAddRoleModal}>
        <DialogContent className="max-w-2xl bg-card border-border text-foreground max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <i className="fas fa-plus-circle text-primary"></i> Create Dynamic Job Role
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddRoleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="role_title">Role Title</Label>
              <Input
                id="role_title"
                value={roleForm.title}
                onChange={(e) => setRoleForm(prev=>({ ...prev, title: e.target.value }))}
                required
                placeholder="e.g. Director of Logistics, Fleet Manager"
                className="border-border rounded-xl bg-background text-foreground h-10"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role_description">Job Description</Label>
              <Input
                id="role_description"
                value={roleForm.description}
                onChange={(e) => setRoleForm(prev=>({ ...prev, description: e.target.value }))}
                placeholder="Brief summary of duties and responsibilities"
                className="border-border rounded-xl bg-background text-foreground h-10"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role_dept">Default Department Mapping</Label>
                <Select 
                  value={roleForm.department} 
                  onValueChange={(val) => setRoleForm(prev=>({ ...prev, department: val }))}
                >
                  <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    {DEPARTMENTS.map(dept => (
                      <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="role_base">Base Security Access Level</Label>
                <Select 
                  value={roleForm.base_role} 
                  onValueChange={(val) => setRoleForm(prev=>({ ...prev, base_role: val }))}
                >
                  <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                    <SelectValue placeholder="Select system access" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    {BASE_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Document Checklist Selection */}
            <div className="space-y-2 border-t border-border/60 pt-4">
              <Label className="font-bold text-sm text-foreground">Compliance Documentation Checklist</Label>
              <p className="text-xs text-muted-foreground mb-3">Select the documents required for staff holding this role.</p>
              
              <div className="grid grid-cols-2 gap-2 max-h-[200px] overflow-y-auto border border-border p-3 rounded-xl bg-muted/20">
                {DOCUMENT_TYPES.map(doc => (
                  <div key={doc.value} className="flex items-center space-x-2.5 py-1">
                    <input
                      type="checkbox"
                      id={`req_doc_${doc.value}`}
                      checked={roleForm.required_documents.includes(doc.value)}
                      onChange={() => toggleDocInRoleForm(doc.value)}
                      className="rounded border-border text-primary focus:ring-primary h-4 w-4 bg-background"
                    />
                    <label htmlFor={`req_doc_${doc.value}`} className="text-xs text-foreground cursor-pointer font-medium select-none">
                      {doc.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter className="border-t border-border/60 pt-4 mt-6">
              <Button type="button" variant="outline" onClick={() => setShowAddRoleModal(false)} className="rounded-xl border-border hover:bg-muted">
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90 text-white rounded-xl px-5">
                Save Custom Role
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DIALOG MODAL - VIEW / VERIFY DOCUMENT PORTAL */}
      <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
        <DialogContent className="max-w-md bg-card border-border text-foreground rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <i className="fas fa-file-shield text-primary"></i> Verification Portal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 text-sm">
            <div className="bg-muted/30 p-3 rounded-xl border border-border">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-muted-foreground uppercase">Staff Profile</span>
                <span className="text-xs font-semibold text-muted-foreground uppercase">Document Class</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <div className="font-bold text-foreground">{selectedStaff?.full_name}</div>
                <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-none font-medium text-xs px-2 py-0.2 rounded">
                  {selectedDoc?.label}
                </Badge>
              </div>
            </div>

            {selectedDoc?.file_url ? (
              <div className="space-y-3">
                <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <i className="far fa-file-pdf text-red-500 text-3xl"></i>
                    <div>
                      <div className="font-bold text-foreground text-xs leading-tight">file_attached.pdf</div>
                      <span className="text-[10px] text-muted-foreground">Uploaded: {selectedDoc.uploaded_at?.split('T')[0] || "just now"}</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs border-border bg-background" onClick={() => window.open(selectedDoc.file_url)}>
                    <i className="fas fa-external-link-alt mr-1"></i> View file
                  </Button>
                </div>
                
                {selectedDoc.verified_by && (
                  <div className="text-[10px] text-muted-foreground leading-normal italic">
                    Status updated to {selectedDoc.status} by {selectedDoc.verified_by} on {selectedDoc.verified_at?.split('T')[0]}
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleDocUploadSubmit} className="space-y-3">
                <Label>File Upload & Expiry Config</Label>
                <div className="border border-dashed border-border p-6 rounded-xl text-center bg-muted/10 hover:bg-muted/20 transition-colors flex flex-col items-center justify-center cursor-pointer">
                  <i className="fas fa-cloud-upload-alt text-2xl text-muted-foreground mb-2"></i>
                  <span className="font-bold text-xs">Click to select files</span>
                  <span className="text-[10px] text-muted-foreground mt-1">Supports PDF, PNG, JPG up to 10MB</span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => setDocUploadForm(prev=>({ ...prev, file_name: e.target.files[0]?.name || '' }))}
                  />
                  {docUploadForm.file_name && (
                    <div className="mt-2 text-xs font-bold text-primary flex items-center gap-1.5">
                      <i className="fas fa-file-alt"></i> {docUploadForm.file_name}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="doc_expiry">Expiration Date (Optional)</Label>
                  <Input
                    id="doc_expiry"
                    type="date"
                    value={docUploadForm.expiry_date}
                    onChange={(e) => setDocUploadForm(prev=>({ ...prev, expiry_date: e.target.value }))}
                    className="border-border rounded-xl bg-background text-foreground h-10"
                  />
                </div>

                <Button type="submit" disabled={docUploadForm.isUploading} className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-10">
                  {docUploadForm.isUploading ? "Uploading..." : "Submit File for Review"}
                </Button>
              </form>
            )}

            {/* Verification Controls */}
            <div className="border-t border-border/60 pt-4 space-y-3">
              <Label className="font-bold">Verify Document Status (Administrators Only)</Label>
              <div className="grid grid-cols-3 gap-2">
                <Button 
                  onClick={() => handleVerifyDocument('verified')} 
                  variant="outline" 
                  className="rounded-xl border-emerald-500/20 text-emerald-500 hover:bg-emerald-500/10 text-xs h-9 font-semibold"
                >
                  <i className="fas fa-check-circle mr-1"></i> Approve
                </Button>
                <Button 
                  onClick={() => handleVerifyDocument('expired')} 
                  variant="outline" 
                  className="rounded-xl border-red-500/20 text-red-500 hover:bg-red-500/10 text-xs h-9 font-semibold"
                >
                  <i className="fas fa-exclamation-triangle mr-1"></i> Expired
                </Button>
                <Button 
                  onClick={() => handleVerifyDocument('missing')} 
                  variant="outline" 
                  className="rounded-xl border-border text-muted-foreground hover:bg-muted text-xs h-9 font-semibold"
                >
                  <i className="fas fa-times mr-1"></i> Reject
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DIALOG MODAL - PAYROLL CONFIGURATION */}
      <Dialog open={showPayrollModal} onOpenChange={setShowPayrollModal}>
        <DialogContent className="max-w-md bg-card border-border text-foreground rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <i className="fas fa-wallet text-primary"></i> Edit Payroll Profile
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePayrollSubmit} className="space-y-4 py-4 text-sm">
            <div className="bg-muted/30 p-3 rounded-xl border border-border">
              <span className="text-[10px] font-bold text-muted-foreground uppercase">Staff Member</span>
              <div className="font-bold text-foreground mt-0.5">{selectedStaff?.full_name}</div>
              <div className="text-xs text-muted-foreground">{selectedStaff?.role_title}</div>
            </div>

            <div className="space-y-2">
              <Label>Pay Type</Label>
              <Select 
                value={payrollForm.pay_type} 
                onValueChange={(val) => setPayrollForm(prev=>({ ...prev, pay_type: val }))}
              >
                <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                  <SelectValue placeholder="Pay Type" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="salary">Salary (Flat / yr)</SelectItem>
                  <SelectItem value="hourly">Hourly Rate</SelectItem>
                  <SelectItem value="per_mile">Per Mile Payout</SelectItem>
                  <SelectItem value="per_load">Per Load (% of Load Revenue)</SelectItem>
                  <SelectItem value="commission">Commission %</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Rate (Value)</Label>
              <Input
                type="number"
                step="any"
                value={payrollForm.pay_rate}
                onChange={(e) => setPayrollForm(prev=>({ ...prev, pay_rate: parseFloat(e.target.value) || 0 }))}
                className="border-border rounded-xl bg-background text-foreground h-10"
              />
              <p className="text-[10px] text-muted-foreground italic leading-normal">
                Input annual value for Salary, hourly rate for Hourly, mileage fee (e.g. 0.65) for Per Mile, percentage integer (e.g. 80) for Per Load / Commission.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select 
                  value={payrollForm.payment_frequency} 
                  onValueChange={(val) => setPayrollForm(prev=>({ ...prev, payment_frequency: val }))}
                >
                  <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                    <SelectValue placeholder="Frequency" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="bi_weekly">Bi-Weekly</SelectItem>
                    <SelectItem value="semi_monthly">Semi-Monthly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select 
                  value={payrollForm.payment_method} 
                  onValueChange={(val) => setPayrollForm(prev=>({ ...prev, payment_method: val }))}
                >
                  <SelectTrigger className="border-border rounded-xl bg-background text-foreground h-10">
                    <SelectValue placeholder="Method" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="wire">Bank Wire Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="border-t border-border/60 pt-4 mt-6">
              <Button type="button" variant="outline" onClick={() => setShowPayrollModal(false)} className="rounded-xl border-border hover:bg-muted">
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90 text-white rounded-xl px-5">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRDepartment;
